import type { JiraPrioritiesResponse, JiraPriorityOption } from '@/types';
import { useQuery } from '@/utils/api';

import { useAxios } from './useAxios';

export interface UseJiraPrioritiesOptions {
  accountKey?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
}

export interface UseJiraPrioritiesResult {
  priorities: JiraPriorityOption[];
}

/**
 * Fetches available priorities from the Jira instance. Priorities are
 * org-level (not project-scoped), so no projectKey/issueType is needed.
 *
 * Disabled until credentials are available (either accountKey for an
 * existing integration or config for setup-time credentials).
 */
export const useJiraPriorities = ({
  accountKey,
  config,
  enabled = true,
}: UseJiraPrioritiesOptions) => {
  const axios = useAxios();

  return useQuery({
    defaultErrorMessage: 'Failed to fetch Jira priorities',
    queryKey: [
      'jira',
      'priorities',
      accountKey ?? 'new',
      config ? JSON.stringify(config) : '',
    ],
    queryFn: async (): Promise<UseJiraPrioritiesResult> => {
      const response = await axios.post<JiraPrioritiesResponse>(
        '/integration/jira/priorities',
        {
          provider: 'jira',
          account: accountKey,
          secret: accountKey ? undefined : config,
        }
      );

      return { priorities: response.data?.priorities ?? [] };
    },
    enabled: enabled && (!!accountKey || !!config),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
